import { createContext } from "react";

import { PersistedRevision, Revision } from "../types/schemas";
import { APITranscript, EditorAction } from "../types/types";

export type TranscriptAction =
  | {
      type: "clientEditorAction";
      editorAction: EditorAction;
      /**
       * the transcript version this editor action was generated from
       * primarily used to catch up to server revisions applied between generation and this dispatch processing
       */
      baseVersion: number;
    }
  | { type: "sentClientRevision"; revisionId: string }
  | { type: "serverRevisions"; revisions: PersistedRevision[] }
  | { type: "serverAck"; revision: PersistedRevision | null }
  | { type: "undo" }
  | { type: "redo" }
  | { type: "prune" }
  | { type: "consumeRevisionEvents"; toIndex: number };

export interface RevisionMeta {
  isUndo?: boolean;
  isRedo?: boolean;
  fromClient?: boolean;
  fromServer?: boolean;
  /**
   * Unlike revision.version which is the version of the transcript after the revision is applied
   * this is the transcript version the revision was based from.
   * Note that because client revisions are applied to local state optimistically without
   * incrementing the version this only gives information about which server version the revision was synced to.
   * We assume multiple client revisions are sequentially correct with regards to causality
   * even though the base version would be the same for all of them.
   */
  baseVersion?: number;
}

export interface CachedRevision {
  revision: Revision | null;
  revisionMeta: RevisionMeta;
}

export interface RevisionEvent {
  revision: Revision;
  revisionMeta: RevisionMeta;
}

export interface TranscriptState {
  /**
   * Zero-based, this represents the greatest version on the server that the client is synced to.
   * There are two ways the client state + version may not equal the server version:
   * 1) There are queued client updates which have not been processed and persisted by the server yet,
   * even though they are already applied to the client state.
   * 2) There are outstanding server revisions that have not been applied to the client state yet,
   * because they were received out of order.
   */
  version: number;

  transcript: APITranscript;

  status: "ready" | "awaitingServerAck";

  /**
   * The id that we assigned to the revision we are currently waiting on for a server ack.
   * Stored as a reference so that we can resolve awaiting status in case of missing the ack message.
   */
  awaitingRevisionId: string | null;

  /**
   * Used as a reference when deciding to send the next revision.
   * Need to make sure the revision we will send has not missed any server revisions
   * The client doesn't need to be at the tip of the server's state to send,
   * but there can't be any gaps that the client revision hasn't been transformed against yet.
   */
  lastAcked: PersistedRevision | null;

  /**
   * Revisions from this client are optimistically applied to client state and sent one at a time to the server.
   * These revisions are transformed to rebase on top of any server revisions that are applied to the local state.
   */
  clientRevisions: (Revision | null)[];

  /**
   * Sparse store of persisted revisions received from the server but not yet applied to client state.
   * Revisions must be applied in order, but we may receive revisions out-of-order, so they are stored here until they can be applied.
   * Revisions originating on the client will never be stored here because they are optimistically applied.
   */
  outstandingRevisions: Record<string, PersistedRevision>;

  /**
   * Sparse store of persisted revisions where the index is the revision's version number.
   * Revisions must be applied in order, but we may receive revisions out-of-order.
   * These revisions have already been applied to the client state.
   */
  cachedRevisions: Record<string, CachedRevision>;

  /**
   * Revisions that have been applied to the client state will be queued here.
   * Mirror of revisions that make it into cachedRevisions, however events will be removed
   * from this queue (FIFO) as they are consumed and propagated to subscribers.
   */
  revisionEvents: RevisionEvent[];

  /** These actions are transformed to rebase on top of server revisions that are applied to the local state. */
  undos: EditorAction[];
  /** These actions are transformed to rebase on top of server revisions that are applied to the local state. */
  redos: EditorAction[];
}

export type RevisionCallback = (revision: RevisionEvent) => void;
export type RevisionSubscribe = (callback: RevisionCallback) => () => void;

export type TranscriptContextValue = TranscriptState & {
  dispatch: React.Dispatch<TranscriptAction>;
  registerRevisionCallback: RevisionSubscribe;
};

export const TranscriptContext = createContext<TranscriptContextValue>({
  version: 0,
  status: "ready",
  awaitingRevisionId: null,
  lastAcked: null,
  clientRevisions: [],
  outstandingRevisions: {},
  cachedRevisions: {},
  revisionEvents: [],
  undos: [],
  redos: [],
  transcript: {} as APITranscript,
  dispatch: () =>
    console.warn(
      "You must wrap this component in TranscriptProvider before using this method.",
    ),
  registerRevisionCallback: () => () => {},
});
